import { useRef, useState } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Download, Upload, Trash2, Copy } from 'lucide-react';
import { WorkflowExportImport } from './WorkflowExportImport';
import { WorkflowCloneDialog } from './WorkflowCloneDialog';
import { toast } from 'sonner';

export function WorkflowManager() {
  const { workflows, currentWorkflow, saveWorkflow, deleteWorkflow, importWorkflow, setCurrentWorkflow } =
    useWorkflowStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [cloneDialog, setCloneDialog] = useState<{
    open: boolean;
    workflowId: string | null;
    workflowName: string;
  }>({ open: false, workflowId: null, workflowName: '' });

  const handleExportCurrent = () => {
    if (!currentWorkflow) {
      toast.error('No workflow selected');
      return;
    }
    try {
      exportWorkflowAsJSON(currentWorkflow);
      toast.success(`Exported "${currentWorkflow.name}"`);
    } catch (error) {
      toast.error('Failed to export workflow');
    }
  };

  const handleExportAll = () => {
    if (workflows.length === 0) {
      toast.error('No workflows to export');
      return;
    }
    try {
      exportAllWorkflows(workflows);
      toast.success(`Exported ${workflows.length} workflows`);
    } catch (error) {
      toast.error('Failed to export workflows');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      importWorkflow(data);
      toast.success(`Imported workflow "${data.name}"`);
    } catch (error) {
      toast.error('Failed to import workflow');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteWorkflow = (workflowId: string) => {
    const workflow = workflows.find((w) => w.id === workflowId);
    if (!workflow) return;

    if (confirm(`Delete workflow "${workflow.name}"?`)) {
      deleteWorkflow(workflowId);
      toast.success('Workflow deleted');
    }
  };

  const handleSelectWorkflow = (workflowId: string) => {
    const workflow = workflows.find((w) => w.id === workflowId);
    if (workflow) {
      setCurrentWorkflow(workflow);
    }
  };

  const handleCloneClick = (workflowId: string, workflowName: string) => {
    setCloneDialog({ open: true, workflowId, workflowName });
  };

  const closeCloneDialog = () => {
    setCloneDialog({ open: false, workflowId: null, workflowName: '' });
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" onClick={handleExportCurrent} disabled={!currentWorkflow}>
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
        <Button size="sm" onClick={handleExportAll} disabled={workflows.length === 0}>
          <Download className="w-4 h-4 mr-2" />
          Export All
        </Button>
        <Button size="sm" onClick={handleImportClick} disabled={isImporting}>
          <Upload className="w-4 h-4 mr-2" />
          {isImporting ? 'Importing...' : 'Import'}
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Workflows List */}
      <div className="space-y-2">
        {workflows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">No workflows yet. Create one to get started.</p>
          </div>
        ) : (
          workflows.map((workflow) => (
            <Card
              key={workflow.id}
              className={`p-4 cursor-pointer ui-lift-card ${
                currentWorkflow?.id === workflow.id
                  ? 'border-primary bg-primary/5'
                  : 'hover:border-primary/50'
              }`}
              onClick={() => handleSelectWorkflow(workflow.id)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {workflow.name}
                  </p>
                  <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                    <span>{workflow.nodes.length} nodes</span>
                    <span>{workflow.edges.length} connections</span>
                    <span>
                      {new Date(workflow.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloneClick(workflow.id, workflow.name);
                    }}
                  >
                    <Copy className="w-4 h-4 text-primary" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteWorkflow(workflow.id);
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Export/Import Section */}
      <div className="pt-4 border-t border-border">
        <WorkflowExportImport />
      </div>

      <WorkflowCloneDialog
        workflowId={cloneDialog.workflowId}
        workflowName={cloneDialog.workflowName}
        open={cloneDialog.open}
        onOpenChange={(open) => setCloneDialog({ ...cloneDialog, open })}
      />
    </div>
  );
}